import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useCallback } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';

import { GET_USER_MONTHLY_PASS_CLAIMED_REWARDS, RESET_USER_MONTHLY_PASS_CLAIMED_REWARDS } from '../../../graphql';

type ClaimedReward = {
  level: number;
  bestTierClaimed: string | null;
  tiersClaimed: string[];
};

type Props = {
  userId: string;
  // Owned by <MonthlyPassDisplay>: the month picked in this sub-section's own selector
  monthId: string;
};

const MonthlyPassRewardsDisplay: React.FC<Props> = ({ userId, monthId }) => {
  const [getClaimedRewards, { data, loading }] = useLazyQuery(GET_USER_MONTHLY_PASS_CLAIMED_REWARDS, {
    fetchPolicy: 'network-only',
  });
  const [resetClaimedRewards, { loading: resetting }] = useMutation(RESET_USER_MONTHLY_PASS_CLAIMED_REWARDS);

  const userMonthlyPass = data?.app?.getUserMonthlyPass?.userMonthlyPass;
  const claimedRewards: ClaimedReward[] = userMonthlyPass?.claimedRewards ?? [];
  // Every track the user has claimed at least once, to offer "reset this track on all levels".
  const claimedTiers = Array.from(new Set(claimedRewards.flatMap((reward) => reward.tiersClaimed)));

  const showRewards = useCallback(() => {
    getClaimedRewards({ variables: { input: { monthId, userId } } });
  }, [getClaimedRewards, monthId, userId]);

  const reset = useCallback(
    (levels?: number[], tiers?: string[]) => {
      const levelScope = levels ? `level ${levels.join(', ')}` : 'ALL levels';
      const tierScope = tiers ? `${tiers.join(', ')} track` : 'all tracks';
      if (
        !window.confirm(
          `Un-claim ${levelScope} (${tierScope}) of monthly pass ${monthId} for this user? Already granted items/currency are NOT taken back.`
        )
      ) {
        return;
      }
      resetClaimedRewards({ variables: { input: { userId, monthId, levels, tiers } } });
    },
    [resetClaimedRewards, userId, monthId]
  );

  return (
    <>
      <div className="d-flex justify-content-start align-items-baseline flex-wrap mb-3" style={{ gap: 8 }}>
        <Button size="sm" onClick={showRewards} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Show claimed rewards'}
        </Button>
        {claimedTiers.map((tier) => (
          <Button
            key={tier}
            size="sm"
            variant="warning"
            disabled={resetting}
            title={`Un-claim the ${tier} track on every level of this month`}
            onClick={() => reset(undefined, [tier])}
          >
            Reset all {tier}
          </Button>
        ))}
        <Button size="sm" variant="danger" onClick={() => reset()} disabled={resetting || claimedRewards.length === 0}>
          {resetting ? <Spinner animation="border" size="sm" /> : 'Reset everything'}
        </Button>
      </div>

      {userMonthlyPass && (
        <div className="mb-2">
          Level {userMonthlyPass.level} — {userMonthlyPass.progressToNextLevel}% to next level — xpBuff x
          {userMonthlyPass.xpBuff}, coinBuff x{userMonthlyPass.coinBuff}
        </div>
      )}

      {userMonthlyPass && claimedRewards.length === 0 ? (
        <div>No claimed level reward for this month</div>
      ) : (
        claimedRewards.length > 0 && (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Best tier claimed</th>
                  <th>Tiers claimed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {claimedRewards.map((reward) => (
                  <tr key={reward.level}>
                    <td>{reward.level}</td>
                    <td>{reward.bestTierClaimed ?? '-'}</td>
                    <td>{reward.tiersClaimed.join(', ')}</td>
                    <td>
                      <div className="d-flex" style={{ gap: 4 }}>
                        {reward.tiersClaimed.map((tier) => (
                          <Button
                            key={tier}
                            size="sm"
                            variant="warning"
                            disabled={resetting}
                            title={`Un-claim only the ${tier} track of level ${reward.level}`}
                            onClick={() => reset([reward.level], [tier])}
                          >
                            Reset {tier}
                          </Button>
                        ))}
                        {reward.tiersClaimed.length > 1 && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={resetting}
                            title={`Un-claim every track of level ${reward.level}`}
                            onClick={() => reset([reward.level])}
                          >
                            Reset all tracks
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )
      )}
    </>
  );
};

export default MonthlyPassRewardsDisplay;
